import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useDidMount } from 'src/hooks/useDidMount';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { CustomerDetailDto, CustomerDto } from 'src/store/customers/customer';
import { mapCustomerDetailToEdit } from 'src/store/customers/customer.map';
import {
  createCustomer,
  deleteCustomer,
  editCustomer,
  fetchCustomers
} from 'src/store/customers/customers.actions';
import { CustomerApi } from 'src/store/customers/customers.api';
import { selectCustomers } from 'src/store/customers/customers.selectors';
import { isLoadingCustomers } from 'src/store/requests/requests.selectors';
import { CustomerForm } from './CustomerForm';

export const CustomersPage = () => {
  const [currentCustomer, setCurrentCustomer] = useState<CustomerDto | null>(
    null
  );
  const [customer, setCustomer] = useState<Nullable<CustomerDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const customers = useSelector(selectCustomers);
  const loadingCustomers = useSelector(isLoadingCustomers);

  useDidMount(() => {
    dispatch(fetchCustomers());
  }, []);

  useEffect(() => {
    if (currentCustomer) {
      setShowDeleteModal(true);
    }
  }, [currentCustomer]);

  const onCancel = () => {
    setCurrentCustomer(null);
    setCustomer(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (customer: CustomerDto) => {
    const response = await CustomerApi.getById(customer.id);
    if (response.ok) {
      setCustomer(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (customer: CustomerDto) => {
    setCurrentCustomer(customer);
  };

  const onConfirmDelete = () => {
    if (currentCustomer) {
      dispatch(deleteCustomer(currentCustomer.id, currentCustomer));
      setCurrentCustomer(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (customer: CustomerDetailDto) => {
    const dto = mapCustomerDetailToEdit(customer);
    if (dto.id) {
      await dispatch(editCustomer(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createCustomer(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('customer.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('customer.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('customer.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={customers}
        loading={loadingCustomers}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell
              count={customers?.length}
              label={t('customer.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${customer ? t('edit') : t('create.verb')} ${t(
          'customer.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <CustomerForm
          customer={customer}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('customer.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('customer.delete')} <q>{currentCustomer?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
